(() => {
  'use strict';

  const DB_NAME = 'tp-direct-create';
  const STORE_NAME = 'handoffs';
  const MAX_AGE_MS = 30 * 60 * 1000;
  // Il configuratore stampe tiene il passaggio finché la sua pagina è aperta (Safari): qui si puliscono solo i vecchi.
  const SWEEP_AGE_MS = 6 * 60 * 60 * 1000;
  const MAX_BATCH_FILES = 300;
  // Stampe foto: misura e carta si scelgono nel configuratore, subito dopo le foto (nessuna finestra prima).
  const ASK_PHOTO_FORMAT_HERE = false;
  const triggerSelector = '[data-tp-direct-create]';
  const PHOTO_CONFIG_PATH = '/pages/configuratore-foto';
  const PHOTO_PRODUCT_ENDPOINT = '/products/stampa-foto-configuratore.js';
  const PHOTO_PRODUCT_ID = 15979180228942;
  let activeTrigger = null;
  let chooser = null;

  const openDb = () => new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('indexeddb-open'));
  });

  const transact = async (mode, callback) => {
    const db = await openDb();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const request = callback(tx.objectStore(STORE_NAME));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('indexeddb-request'));
        tx.onerror = () => reject(tx.error || new Error('indexeddb-transaction'));
      });
    } finally {
      db.close();
    }
  };

  const sweepExpiredHandoffs = async () => {
    const db = await openDb();
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const request = tx.objectStore(STORE_NAME).openCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (!cursor) return;
          const createdAt = Number(cursor.value && cursor.value.createdAt);
          if (Number.isFinite(createdAt) && Date.now() - createdAt > SWEEP_AGE_MS) cursor.delete();
          cursor.continue();
        };
        request.onerror = () => reject(request.error || new Error('indexeddb-sweep'));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error || new Error('indexeddb-sweep-transaction'));
        tx.onabort = () => reject(tx.error || new Error('indexeddb-sweep-abort'));
      });
    } finally {
      db.close();
    }
  };

  const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const setStatus = (message, error = false) => {
    const root = activeTrigger && activeTrigger.closest('[data-tp-direct-create-scope]');
    const status = (root && root.querySelector('[data-tp-direct-create-status]'))
      || document.querySelector('[data-tp-direct-create-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', error);
  };

  const createChooser = () => {
    if (chooser) return chooser;
    chooser = document.createElement('input');
    chooser.type = 'file';
    chooser.hidden = true;
    chooser.tabIndex = -1;
    chooser.addEventListener('change', handleSelection);
    document.body.appendChild(chooser);
    return chooser;
  };

  const validate = (files, multiple) => {
    if (!files.length) return 'Nessun file selezionato.';
    if (!multiple && files.length !== 1) return 'Seleziona una sola fotografia.';
    if (multiple && files.length > MAX_BATCH_FILES) return `Puoi caricare fino a ${MAX_BATCH_FILES} fotografie alla volta.`;
    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
    const invalid = files.find((file) => !allowed.has(file.type));
    if (invalid) return 'Sono accettate fotografie JPG, PNG o WEBP.';
    const maxMb = multiple ? 50 : 20;
    const tooLarge = files.find((file) => file.size > maxMb * 1024 * 1024);
    if (tooLarge) return `Il file ${tooLarge.name} supera il limite di ${maxMb} MB.`;
    return '';
  };

  const fetchPhotoFormats = async () => {
    const response = await fetch(PHOTO_PRODUCT_ENDPOINT, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`photo-product-${response.status}`);
    const product = await response.json();
    if (Number(product.id) !== PHOTO_PRODUCT_ID || !Array.isArray(product.variants)) {
      throw new Error('photo-product-mismatch');
    }
    const formats = [];
    product.variants.forEach((variant) => {
      if (variant.available && variant.option1 && !formats.includes(variant.option1)) formats.push(variant.option1);
    });
    if (!formats.length) throw new Error('photo-formats-empty');
    return formats;
  };

  const choosePhotoFormat = async () => {
    const formats = await fetchPhotoFormats();
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.className = 'tp-photo-format-dialog';
      dialog.setAttribute('aria-labelledby', 'tp-photo-format-title');
      dialog.style.cssText = 'width:min(92vw,460px);max-width:460px;border:0;border-radius:14px;padding:0;box-shadow:0 20px 60px rgba(0,0,0,.28);';

      const form = document.createElement('form');
      form.method = 'dialog';
      form.style.cssText = 'display:grid;gap:16px;padding:24px;background:#fff;color:#102f3f;';

      const title = document.createElement('h2');
      title.id = 'tp-photo-format-title';
      title.textContent = '2 di 3 · Scegli il formato';
      title.style.cssText = 'margin:0;font-size:1.5rem;line-height:1.2;';

      const note = document.createElement('p');
      note.textContent = 'Il formato sarà applicato a tutte le foto. Potrai cambiarlo per ogni immagine nel configuratore.';
      note.style.cssText = 'margin:0;line-height:1.5;';

      const label = document.createElement('label');
      label.htmlFor = 'tp-photo-format-select';
      label.textContent = 'Formato di stampa';
      label.style.cssText = 'font-weight:700;';

      const select = document.createElement('select');
      select.id = 'tp-photo-format-select';
      select.required = true;
      select.style.cssText = 'width:100%;min-height:48px;margin-top:8px;padding:10px 12px;border:1px solid #9aa8ae;border-radius:8px;background:#fff;color:#102f3f;font:inherit;';
      formats.forEach((format) => {
        const option = document.createElement('option');
        option.value = format;
        const printableFormat = format.replaceAll('.', ',');
        option.textContent = format.endsWith('x')
          ? `Lato corto ${printableFormat.slice(0, -1)} cm`
          : printableFormat.replace('x', ' × ') + ' cm';
        select.appendChild(option);
      });
      label.appendChild(select);

      const actions = document.createElement('div');
      actions.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.textContent = 'Annulla';
      cancel.style.cssText = 'min-height:44px;padding:10px 16px;border:1px solid #9aa8ae;border-radius:8px;background:#fff;color:#102f3f;font:inherit;';
      const submit = document.createElement('button');
      submit.type = 'submit';
      submit.textContent = 'Apri il configuratore';
      submit.style.cssText = 'min-height:44px;padding:10px 18px;border:1px solid #0b3345;border-radius:8px;background:#0b3345;color:#fff;font:inherit;font-weight:700;';
      actions.append(cancel, submit);
      form.append(title, note, label, actions);
      dialog.appendChild(form);

      const finish = (value) => {
        dialog.remove();
        resolve(value || '');
      };
      form.addEventListener('submit', (event) => { event.preventDefault(); dialog.close(select.value); });
      cancel.addEventListener('click', () => dialog.close(''));
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); dialog.close(''); });
      dialog.addEventListener('close', () => finish(dialog.returnValue), { once: true });
      document.body.appendChild(dialog);
      dialog.showModal();
      select.focus();
    });
  };

  async function handleSelection(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length || !activeTrigger) return;
    const multiple = activeTrigger.dataset.tpMultiple === 'true';
    const error = validate(files, multiple);
    if (error) {
      setStatus(error, true);
      activeTrigger = null;
      return;
    }

    const destination = activeTrigger.dataset.tpDestination || activeTrigger.getAttribute('href') || '';
    if (!destination.startsWith('/pages/')) {
      setStatus('Percorso di configurazione non disponibile.', true);
      activeTrigger = null;
      return;
    }

    const isPhotoConfigurator = new URL(destination, location.origin).pathname === PHOTO_CONFIG_PATH;
    let format = '';
    if (isPhotoConfigurator && ASK_PHOTO_FORMAT_HERE) {
      try {
        setStatus(`${files.length} foto selezionate. Ora scegli il formato.`);
        format = await choosePhotoFormat();
      } catch (err) {
        console.error('TP photo format selector', err);
        setStatus('Non è stato possibile caricare i formati. Riprova.', true);
        activeTrigger = null;
        return;
      }
      if (!format) {
        setStatus('');
        activeTrigger = null;
        return;
      }
    }

    const id = uid();
    const record = {
      id,
      createdAt: Date.now(),
      product: isPhotoConfigurator ? { id: PHOTO_PRODUCT_ID, handle: 'stampa-foto-configuratore' } : null,
      format,
      files: files.map((file) => ({
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        blob: file,
      })),
    };

    try {
      setStatus(`${files.length} foto selezionate. Le importiamo nel configuratore…`);
      await transact('readwrite', (store) => store.put(record));
      const url = new URL(destination, location.origin);
      url.searchParams.set('tp_handoff', id);
      location.assign(`${url.pathname}${url.search}`);
    } catch (err) {
      console.error('TP direct create handoff', err);
      setStatus('Non è stato possibile preparare le fotografie. Riprova.', true);
      activeTrigger = null;
    }
  }

  const waitForInput = async (selector, timeoutMs = 10000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const input = document.querySelector(selector);
      if (input) return input;
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return null;
  };

  const waitForOption = async (selector, value, timeoutMs = 15000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const select = document.querySelector(selector);
      if (select && Array.from(select.options || []).some((option) => option.value === value)) return select;
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return null;
  };

  const waitForFunction = async (name, timeoutMs = 15000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      if (typeof globalThis[name] === 'function') return globalThis[name];
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return null;
  };

  const consumeHandoff = async () => {
    const url = new URL(location.href);
    const id = url.searchParams.get('tp_handoff');
    if (!id) return;

    try {
      const record = await transact('readonly', (store) => store.get(id));
      if (!record || !Array.isArray(record.files) || !record.files.length || Date.now() - record.createdAt > MAX_AGE_MS) {
        throw new Error('handoff-missing');
      }

      const photoMode = location.pathname === '/pages/configuratore-foto';
      const selector = photoMode ? '#handler-file' : '#tpv8-image-upload';
      const input = await waitForInput(selector);
      if (!input) throw new Error('handoff-input-missing');

      const transfer = new DataTransfer();
      record.files.forEach((item) => {
        transfer.items.add(new File([item.blob], item.name, {
          type: item.type,
          lastModified: item.lastModified || Date.now(),
        }));
      });
      input.files = transfer.files;
      if (photoMode) {
        if (!record.product || Number(record.product.id) !== PHOTO_PRODUCT_ID || !record.format) {
          throw new Error('handoff-photo-metadata');
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const sizeSelect = await waitForOption('#option-size-default', record.format);
        const materialSelect = await waitForInput('#option-material-default', 15000);
        const applyDefaults = await waitForFunction('setDefaultOptions', 15000);
        if (!sizeSelect || !materialSelect || !applyDefaults) throw new Error('handoff-photo-options');
        sizeSelect.value = record.format;
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        if (materialSelect.options.length && materialSelect.selectedIndex < 0) materialSelect.selectedIndex = 0;
        applyDefaults();
      } else {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      await transact('readwrite', (store) => store.delete(id));
      url.searchParams.delete('tp_handoff');
      history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (err) {
      console.error('TP direct create consume', err);
      const message = document.createElement('p');
      message.className = 'tp-direct-create-error';
      message.setAttribute('role', 'alert');
      message.textContent = 'Non è stato possibile recuperare le fotografie. Selezionale di nuovo.';
      (document.querySelector('main') || document.body).prepend(message);
    }
  };

  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Space' && event.key !== ' ' && event.key !== 'Space' && event.key !== 'Spacebar') return;
    const trigger = event.target.closest(`${triggerSelector}, [data-tp-config-upload="1"]`);
    if (!trigger) return;
    event.preventDefault();
    trigger.click();
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(triggerSelector);
    if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;


    event.preventDefault();
    activeTrigger = trigger;
    const input = createChooser();
    const multiple = trigger.dataset.tpMultiple === 'true';
    input.multiple = multiple;
    input.accept = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
    setStatus('');
    input.click();
  });

  const initializeHandoff = () => {
    sweepExpiredHandoffs().catch((error) => console.warn('TP handoff cleanup', error));
    consumeHandoff();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHandoff, { once: true });
  } else {
    initializeHandoff();
  }
})();